import React, { useEffect, useState } from "react";
import { CreditCard, QrCode } from "lucide-react";
import { AppModal, PanelButton } from "../ui";
import StudentSelfPay from "../Students/StudentSelfPay";
import QrPayModal from "./QrPayModal";
import { qrTakesFees } from "../../hooks/useStudentPendingFees";

/**
 * Paying a fee the student owes, by whichever way the institute takes money.
 *
 * One component for every place a student can start paying — My Fees, the
 * top-nav Pay Fees menu, the dashboard card — so they cannot disagree about
 * which methods exist. They did: the menu and the card knew only the card
 * gateway, so a student at a QR-only institute had no way to pay from either.
 *
 *   gateway only → the Razorpay sheet (StudentSelfPay), exactly as before
 *   QR only      → the QR dialog
 *   both         → the student picks
 *
 * `target`: `{ courseAssignmentId, courseName, pending, defaultAmount }`, plus
 * optionally `method: 'qr'` (arrived from the QR itself — skip the choice) and
 * `choices` (every course owed on, for the QR dialog's "This payment is for").
 *
 * `waitingFor(caId)` is what the student already sent by QR for that course and
 * is not yet approved — offered again as "owed", it would be paid twice. The
 * server holds the same line; this just says so before the form opens.
 */
export default function FeePayFlow({
  target, onClose, student, paymentEnabled, qrConfig, waitingFor, onPaid, onQrSubmitted, onBlocked,
}) {
  const qrForFees = qrTakesFees(qrConfig);
  const [method, setMethod] = useState(null);

  useEffect(() => {
    if (!target) { setMethod(null); return; }
    if (target.method === "qr" && qrForFees) setMethod("qr");
    else if (paymentEnabled && qrForFees) setMethod("choose");
    else if (paymentEnabled) setMethod("online");
    else if (qrForFees) setMethod("qr");
    else setMethod(null);
  }, [target, paymentEnabled, qrForFees]);

  // The QR form's ceiling, less what is already waiting for approval.
  const qrMax = (t) => Math.max(0, (Number(t?.pending) || 0) - (waitingFor ? waitingFor(t?.courseAssignmentId) : 0));
  const qrChoices = target?.choices
    ?.map((c) => ({ ...c, max: qrMax(c) }))
    .filter((c) => c.max > 0)
    .map((c) => ({ ...c, defaultAmount: Math.min(Number(c.defaultAmount) || c.max, c.max) }));
  const max = qrMax(target);

  useEffect(() => {
    if (method !== "qr") return;
    const nothingLeft = target?.choices ? !qrChoices?.length : max <= 0;
    if (nothingLeft) {
      onBlocked?.("A payment you sent for this is already waiting for your institute to approve it.");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- decided once per opening
  }, [method]);

  return (
    <>
      <AppModal show={method === "choose"} onHide={onClose} size="sm" title="How would you like to pay?">
        <div className="tw-flex tw-flex-col tw-gap-2.5">
          <PanelButton onClick={() => setMethod("online")}>
            <span className="tw-flex tw-items-start tw-gap-3 tw-text-left">
              <CreditCard size={20} className="tw-mt-0.5 tw-shrink-0 tw-text-primary" />
              <span className="tw-flex tw-flex-col tw-gap-0.5">
                <span className="tw-text-sm tw-font-semibold tw-text-content">Pay online</span>
                <span className="tw-text-xs tw-text-content-muted">Card, UPI or net banking — confirmed instantly.</span>
              </span>
            </span>
          </PanelButton>
          <PanelButton onClick={() => setMethod("qr")}>
            <span className="tw-flex tw-items-start tw-gap-3 tw-text-left">
              <QrCode size={20} className="tw-mt-0.5 tw-shrink-0 tw-text-primary" />
              <span className="tw-flex tw-flex-col tw-gap-0.5">
                <span className="tw-text-sm tw-font-semibold tw-text-content">Scan the institute's UPI QR</span>
                <span className="tw-text-xs tw-text-content-muted">Pay from any UPI app and upload the screenshot — counted once the institute approves it.</span>
              </span>
            </span>
          </PanelButton>
        </div>
      </AppModal>

      <StudentSelfPay
        student={student}
        courseAssignmentId={target?.courseAssignmentId || null}
        pendingFees={target?.pending}
        defaultAmount={target?.defaultAmount ?? null}
        courseName={target?.courseName}
        show={method === "online"}
        onHide={onClose}
        onPaymentSuccess={() => { onClose(); onPaid?.(); }}
      />

      {qrForFees && (
        <QrPayModal
          show={method === "qr" && (target?.choices ? qrChoices?.length > 0 : max > 0)}
          onHide={onClose}
          purpose="fee_payment"
          courseAssignmentId={target?.courseAssignmentId || undefined}
          courseName={target?.courseName}
          amount={Math.min(Number(target?.defaultAmount) || max, max)}
          maxAmount={max}
          choices={target?.choices ? qrChoices : undefined}
          config={qrConfig}
          onSubmitted={(payment, message) => { onClose(); onQrSubmitted?.(payment, message); }}
        />
      )}
    </>
  );
}
